<template>
  <div class="results10-wrap">

    <div class="topbar">
      <div class="topbar-inner">
        <div class="brand">
          <div class="brand-mark"></div>
          <div class="brand-text">SEQ <small>Swiss Eating Quiz · Your sustainability story</small></div>
        </div>
        <div class="topbar-date">09.05.2026</div>
      </div>
    </div>

    <div class="hero">
      <div class="hero-inner">
        <div class="hero-eyebrow">Your sustainability story</div>
        <h1 class="hero-title">Behind every meal you ate this week, there is a <em>story</em>.</h1>
        <p class="hero-sub">
          A field, a farmer, a forest somewhere, a river somewhere else. Your food is connected to all of them. This report walks through what your eating habits mean for the planet and for the people who feed you, with a focus on what's already working in your life. No grades, no guilt. Just a clearer picture of the world your fork is part of.
        </p>
      </div>
    </div>

    <!-- 01: WINS -->
    <section class="section">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-eyebrow">01 · Starting from where you are</div>
          <h2 class="section-title">First, the <em>good news</em>.</h2>
          <p class="section-lede">
            Before we look at anything that might be worth changing, let's pause on what you're already doing well. There's quite a lot.
          </p>
        </div>

        <div class="wins-list">
          <div v-for="w in wins" :key="w.title" class="win">
            <div class="win-icon">{{ w.icon }}</div>
            <div>
              <div class="win-title">{{ w.title }}</div>
              <div class="win-body">{{ w.body }}</div>
            </div>
          </div>
        </div>

        <div class="pull">
          Sustainability isn't a finish line you cross. It's a direction you walk in. You're already walking.
          <div class="pull-attrib">A short thought before we look at the harder parts</div>
        </div>
      </div>
    </section>

    <!-- 02: BIG PICTURE -->
    <section class="section warm">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-eyebrow">02 · The big numbers</div>
          <h2 class="section-title">What does your <em>year of eating</em> look like, in numbers?</h2>
          <p class="section-lede">
            Here are three numbers that capture the environmental scale of how you eat. They're rough estimates, with some uncertainty, but they're in the right neighbourhood.
          </p>
        </div>

        <div class="stat-hero">
          <div class="stat-hero-label">Your dietary carbon footprint, one year</div>
          <div class="stat-hero-value">2.1<span class="unit">tonnes CO₂e</span></div>
          <div class="stat-hero-body">
            That's roughly the same as driving from Bern to Lisbon and back, in a small car, over a year of meals. About 25 % of your total personal climate footprint comes from food. It's not the largest piece (housing and transport usually are), but it's the most flexible. Diet is the one footprint you renegotiate three times a day.
          </div>
        </div>

        <div class="three-stats">
          <div v-for="s in threeStats" :key="s.label" class="stat-tile">
            <div class="stat-tile-icon">{{ s.icon }}</div>
            <div class="stat-tile-value" :class="s.status">{{ s.value }}<span class="unit"> {{ s.unit }}</span></div>
            <div class="stat-tile-label">{{ s.label }}</div>
          </div>
        </div>

        <div class="prose prose--spaced">
          <p>If you're surprised these numbers exist, you're not alone. Most people don't think of food this way. But the basic idea is simple: every plate you eat sits inside a longer story of land, water, energy, and labour. The story isn't always sad, and it isn't always heroic. Mostly it's just there, quietly, behind every bite.</p>
          <p>The good news is that small choices ripple. Switching one meal a week from beef to lentils saves more carbon over a year than recycling for the same period. Eating seasonally in summer is the equivalent of skipping one short-haul flight every few years. None of this requires becoming a different person. It just requires noticing what's on the plate.</p>
        </div>
      </div>
    </section>

    <!-- 03: FOOTPRINT BREAKDOWN -->
    <section class="section">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-eyebrow">03 · Where it all comes from</div>
          <h2 class="section-title">What in your diet does the <em>heavy lifting</em>?</h2>
          <p class="section-lede">
            Not all foods carry the same environmental weight. Here's how your year of meals breaks down by carbon impact, sorted from largest to smallest.
          </p>
        </div>

        <div class="footprint-strip">
          <div v-for="row in footprintRows" :key="row.name" class="footprint-row">
            <div class="footprint-name"><span class="footprint-name-icon">{{ row.icon }}</span>{{ row.name }}</div>
            <div class="footprint-bar"><div class="footprint-bar-fill" :class="row.size" :style="{ width: row.width + '%' }"></div></div>
            <div class="footprint-value">{{ row.value }}</div>
          </div>
        </div>

        <div class="prose">
          <p>There's a striking pattern in this list. The <strong>top three contributors</strong> are dairy, meat, and coffee. Together, they're responsible for about <strong>two thirds</strong> of your dietary footprint. The bottom three, vegetables, fruit, and plant proteins, contribute very little.</p>
          <p>The most interesting line, at the very bottom, is plant proteins: lentils, chickpeas, beans, tofu. You barely eat any. They're also the lowest-impact protein source on the planet. If a chef invented a food that delivered protein, fibre, iron, and folate at one-tenth the climate cost of meat, you'd hear about it on the news. That food exists. It's been around for thousands of years. It's called a bean.</p>
          <p>None of this means you should stop eating dairy or meat or drinking coffee. It just means that, if you ever feel like making a change, you now know which knobs move the most.</p>
        </div>
      </div>
    </section>

    <!-- 04: MEET THE PRODUCERS -->
    <section class="section dark">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-eyebrow">04 · Meet the people behind your plate</div>
          <h2 class="section-title">Faces and lives in your <em>shopping basket</em>.</h2>
          <p class="section-lede">
            Food doesn't arrive in supermarkets. It arrives from somewhere, picked or milked or processed by someone. These are short, slightly fictionalised but representative portraits of people whose work shows up on your plate this week. The point is not to make you feel guilty. The point is to make them visible.
          </p>
        </div>

        <div v-for="story in stories" :key="story.name" class="story-card">
          <div class="story-header">
            <div class="story-flag">{{ story.flag }}</div>
            <h3 class="story-name">{{ story.name }}</h3>
            <div class="story-role">{{ story.role }}</div>
          </div>
          <div class="story-body">
            <p v-for="(p, i) in story.paragraphs" :key="i">{{ p }}</p>
            <div class="story-link"><strong>Why this matters for you:</strong> {{ story.matters }}</div>
          </div>
        </div>

        <div class="pull">
          You can't fix any of this alone. You're not supposed to. But knowing who's at the other end of your fork makes it a little harder to pretend they're not there.
        </div>
      </div>
    </section>

    <!-- 05: WORLD MAP -->
    <section class="section soil">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-eyebrow">05 · A world map of your week</div>
          <h2 class="section-title">Your <em>plate</em>, on a map.</h2>
          <p class="section-lede">
            Most people imagine their food comes from "around here." Some of it does. Most of it doesn't. Here's a rough map of where your weekly groceries probably travelled from.
          </p>
        </div>

        <div class="map-card">
          <p class="map-intro">
            On average across a year, your food touches at least 15 different countries. Some of those connections are obvious (coffee from somewhere tropical). Others are hidden: the soy that feeds your local chicken, the wheat in your bread, the salt on your dinner. Here are the main ones.
          </p>

          <div class="map-list">
            <div v-for="m in mapItems" :key="m.flag" class="map-item">
              <div class="map-flag">{{ m.flag }}</div>
              <div class="map-food">{{ m.food }}</div>
              <div class="map-note">{{ m.note }}</div>
            </div>
          </div>
        </div>

        <div class="prose">
          <p>This map isn't a guilty conscience. It's a working map of one Swiss eater's connection to the world. Even a perfectly "local" Swiss diet has a long tail of imports hidden inside it: animal feed, oils, spices, the cocoa in your chocolate, the salt on your egg.</p>
          <p>The point is that food sovereignty (Switzerland feeding itself) and food sustainability (the planet feeding everyone) are different things. The first is mostly about Swiss agriculture; the second runs through the entire global system. Both are real, and you are part of both.</p>
        </div>
      </div>
    </section>

    <!-- 06: SDGs -->
    <section class="section">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-eyebrow">06 · Global goals your meals connect to</div>
          <h2 class="section-title">Your eating is part of the world's <em>biggest project</em>.</h2>
          <p class="section-lede">
            In 2015, all 193 countries in the United Nations adopted 17 Sustainable Development Goals, a list of the most important things humanity wants to achieve by 2030. Ten of those 17 goals are directly connected to food. Here they are, in their official colours, and how your eating habits connect to each.
          </p>
        </div>

        <div class="sdg-intro">
          <p>Each tile below shows one UN Sustainable Development Goal that your diet touches. The colour is the goal's official UN colour, used by every country, organisation, and report aligned with the 2030 Agenda. The note at the bottom of each tile says, plainly, how your eating habits connect.</p>
        </div>

        <div class="sdg-grid">
          <div v-for="s in sdgs" :key="s.num" class="sdg-tile" :class="`sdg-${s.num}`">
            <div class="sdg-num">{{ String(s.num).padStart(2, '0') }}</div>
            <div class="sdg-title">{{ s.title }}</div>
            <div class="sdg-body">{{ s.body }}</div>
          </div>
        </div>

        <div class="prose prose--spaced">
          <p>You don't need to memorise these. You just need to know, the next time you reach for a Bio label or a Max Havelaar coffee, that the choice connects to one of the most ambitious projects humanity has ever taken on. Your grocery shop is part of the 2030 Agenda. Quietly, but really.</p>
        </div>
      </div>
    </section>

    <!-- 07: SURPRISING FACTS -->
    <section class="section warm">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-eyebrow">07 · Things worth knowing</div>
          <h2 class="section-title">A few <em>surprising</em> facts about your week of eating.</h2>
          <p class="section-lede">
            Not warnings. Not advice. Just things most people don't know about their food until someone tells them.
          </p>
        </div>

        <div v-for="f in facts" :key="f.title" class="card">
          <div class="card-eyebrow">Surprising fact</div>
          <div class="card-title">{{ f.title }}</div>
          <div class="prose prose--card">
            <p v-for="(p, i) in f.paragraphs" :key="i">{{ p }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 08: CHOICES -->
    <section class="section leaf">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-eyebrow">08 · Choices, not commands</div>
          <h2 class="section-title">Some <em>nudges</em>, if you want them.</h2>
          <p class="section-lede">
            Here are concrete changes you could make. Each one is tagged by difficulty (easy, medium, harder) and shows roughly what it would mean for your annual footprint. You don't need to make any of them. You don't need to make all of them. Pick one. Or none.
          </p>
        </div>

        <div class="choices-grid">
          <div v-for="c in choices" :key="c.title" class="choice">
            <span class="choice-tag" :class="c.tagClass">{{ c.tag }}</span>
            <div class="choice-title">{{ c.title }}</div>
            <div class="choice-body">{{ c.body }}</div>
            <div class="choice-impact">{{ c.impact }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- 09: A WEEK -->
    <section class="section sky">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-eyebrow">09 · One week, gently shifted</div>
          <h2 class="section-title">What a <em>greener week</em> could look like.</h2>
          <p class="section-lede">
            Not a meal plan. Just a sketch of what one week could look like if you shifted a few small things. Use it, ignore it, or steal one idea.
          </p>
        </div>

        <div class="card">
          <div class="card-eyebrow">A possible week</div>
          <div class="card-title">A meatless Monday, a legume Wednesday, a fish Friday</div>
          <p class="week-intro">No meat on Monday, lentils for dinner on Wednesday, fish on Friday, and seasonal vegetables on the side most days. Everything else: as you already do it.</p>

          <div class="week-grid">
            <div v-for="d in weekDays" :key="d.name" class="day">
              <div class="day-name">{{ d.name }}</div>
              <div class="day-icon">{{ d.icon }}</div>
              <div class="day-text">{{ d.text }}</div>
            </div>
          </div>

          <p class="week-footer">
            Over a year, a week like this would save roughly 350 to 450 kg of CO₂e compared with your current pattern, plus probably a bit of money. The legumes alone would do most of the work.
          </p>
        </div>

        <div class="promise">
          <div class="promise-eyebrow">A small promise</div>
          <div class="promise-title">No grades, no tracking, no guilt.</div>
          <div class="promise-body">
            <p>If you take the Swiss Eating Quiz again in 3 months, we'll show you what changed. That's it. We won't email you reminders or follow-up nudges. We won't tell you you're doing badly. We won't ask if you really tried.</p>
            <p>Real change happens slowly and quietly, usually in households around dinner tables, not in spreadsheets. Whatever you do with this report is between you and your fridge.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 10: BIG IDEA -->
    <section class="section cream">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-eyebrow">10 · One last thought</div>
          <h2 class="section-title">The point isn't <em>perfection</em>.</h2>
          <p class="section-lede">
            It's connection. Knowing where your food comes from. Knowing what it does to the planet. Knowing the people involved. Acting, sometimes, on what you know.
          </p>
        </div>

        <div class="prose prose--large">
          <p>If you take only one thing from this report, take this. You are already part of a sustainable food system in many small ways: the water you drink, the vegetables you cook, the Swiss dairy you buy, the bottled drinks you don't. These are not heroic acts. They are simply you living your life. They count anyway.</p>
          <p>The harder parts (your coffee, your dairy load, the imported tomato in February, the bananas without a fair-trade label) are not failures. They are invitations. You can pick them up one at a time, as the year goes on. Or not. The system you participate in is also the system you can quietly help bend.</p>
          <p>And the next time you make a coffee, picture Maria on the other end. The next time you cut into a Gruyère, picture Heinz. The next time you peel a banana, picture Lucia. You're not responsible for their lives. But you're connected to them, and they to you, through what's on your plate. Acting on that connection, even a little, is what sustainability really is.</p>
        </div>

        <div class="pull pull--large">
          Eating is the most political act we do three times a day. Make it a thoughtful one.
          <div class="pull-attrib">Adapted from Wendell Berry, American farmer and writer</div>
        </div>
      </div>
    </section>

    <div class="footer">
      <div class="footer-inner">
        <div>
          <div class="brand">
            <div class="brand-mark"></div>
            <div class="brand-text">SEQ <small>Swiss Eating Quiz · Your sustainability story</small></div>
          </div>
          <p>
            The Swiss Eating Quiz is a research instrument developed at Bern University of Applied Sciences. This report uses Life-Cycle Assessment data from Agroscope, Eaternity, FAO, and the EAT-Lancet Commission. The producer portraits in Section 04 are representative composites, drawn from real conditions in each region but not real individuals. The UN Sustainable Development Goal colours are official, used worldwide.
          </p>
        </div>
        <div class="footer-meta">
          Participant report<br>
          v0.1 · May 2026<br>
          BFH Department of Health
        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'Results10',
  data() {
    return {
      wins: [
        { icon: '💧', title: 'You drink water like you mean it.', body: "About 2 litres a day, and almost no sweetened drinks. From an environmental angle, this is one of the most under-celebrated good habits a person can have. Tap water in Switzerland has a footprint so small it's almost zero. Every fizzy drink you didn't have meant a bit less plastic, a bit less sugar transport, a bit less energy. Multiply that by a year, and you've quietly avoided buying around 100 bottles of sweetened drinks." },
        { icon: '🥬', title: 'You eat your vegetables.', body: "Eighteen portions a week is close to the Swiss recommendation, and you do it with variety: leafy greens, roots, tomatoes, alliums, crucifers. Vegetables are the climate-friendliest foods on the planet. Every meal where they show up is a small good thing, even if you don't think of it that way." },
        { icon: '🍎', title: 'You eat fruit regularly.', body: 'About 12 portions a week, mostly apples, pears, and bananas. The apples and pears especially are likely Swiss or European: short trips, modest footprint, real cultural ties to local orchards. Fruit is one of the rare cases where a "moderate, regular" pattern is more sustainable than chasing exotic superfruits from far away.' },
        { icon: '🥛', title: 'Most of your dairy is Swiss.', body: "About 95 % of Swiss dairy stays Swiss. Your yoghurt, your cheese, your milk: they likely come from cows you could see from a hiking trail. Swiss dairy carries the strongest animal welfare laws on the planet. The cows live longer, get more outdoor time, and don't receive growth hormones. None of that is automatic; you and Swiss consumers like you keep that system working." },
        { icon: '🐟', title: "You're inadvertently helping the oceans.", body: "You reported eating no fish, which we'd normally flag as a nutrition gap (and we will, gently, later). But from an ocean perspective, you are not contributing to overfishing or bycatch or the difficult labour conditions in industrial seafood. That's a real thing, even if it wasn't your intention." },
      ],
      threeStats: [
        { icon: '🌍', value: '1,840', unit: 'm²', status: 'warn', label: "Land used somewhere on Earth to feed you for a year. That's about a quarter of a football pitch." },
        { icon: '💧', value: '1,420', unit: 'm³', status: 'warn', label: "Water used to grow your food, including rain that fell on fields in other countries. About 8 swimming pools' worth." },
        { icon: '🛒', value: '52', unit: '%', status: 'bad', label: 'Of your food, by weight, came from outside Switzerland. The other half stayed local.' },
      ],
      footprintRows: [
        { icon: '🥛', name: 'Dairy', size: 'lg', width: 100, value: '560 kg' },
        { icon: '🥩', name: 'Meat & animal proteins', size: 'lg', width: 88, value: '490 kg' },
        { icon: '☕', name: 'Coffee', size: 'md', width: 57, value: '320 kg' },
        { icon: '🌾', name: 'Grains & bread', size: 'sm', width: 43, value: '240 kg' },
        { icon: '🥬', name: 'Vegetables', size: 'sm', width: 34, value: '190 kg' },
        { icon: '🍎', name: 'Fruit', size: 'sm', width: 29, value: '160 kg' },
        { icon: '🌱', name: 'Plant proteins', size: 'xs', width: 3, value: '15 kg' },
      ],
      stories: [
        {
          flag: '🇨🇭 Switzerland · Emmental region',
          name: 'Heinz, 58',
          role: 'Dairy farmer, 32 cows, fourth generation on the farm',
          paragraphs: [
            'Heinz gets up at 4:45 every morning. He has been doing this for 40 years and his father did it before him. The 32 Brown Swiss and Simmental cows on his farm produce the milk that becomes the Emmentaler and Gruyère in your fridge. Swiss law guarantees those cows pasture time, decent space, no growth hormones. Heinz cares about this; most Swiss dairy farmers do.',
            "What Heinz worries about is the future. Swiss dairy prices have been squeezed for two decades. Each year a few hundred Swiss dairy farms close. His son took an apprenticeship as an electrician because the farm couldn't promise a living. When you buy Swiss cheese, you keep Heinz in the work he loves. When you don't, that work slowly disappears from Swiss valleys.",
          ],
          matters: 'Of all your dairy choices, the one labelled with a Swiss origin (Migros Schweizer Milch, Coop Naturaplan, AOP cheeses) keeps the system Heinz works in alive.',
        },
        {
          flag: '🇧🇷 Brazil · Minas Gerais',
          name: 'Maria, 41',
          role: 'Coffee picker on a 200-hectare plantation',
          paragraphs: [
            "Maria works the harvest season, roughly May to September. She picks coffee beans by hand, mostly Arabica. On a good day she earns the equivalent of CHF 11. Her work feeds her two children and her mother. The estate she works on is mid-sized and reasonably well-run, but the price she's paid is set globally, by traders in New York and futures contracts she'll never see.",
            'Of every Swiss franc you spend on coffee, between 5 and 10 centimes reach the country where the coffee was grown. Less than 1 centime reaches Maria herself. The Max Havelaar fairtrade label, when it\'s there, roughly doubles that share. Direct-trade and origin-specific coffees push it higher still.',
            "Maria isn't asking for charity. She's asking for the same thing Heinz is: that the work she does is recognised, paid fairly, and visible to the person on the other end.",
          ],
          matters: 'Your 4 cups a day add up to about 5 kg of coffee per year. If even half of it bore a Max Havelaar or similar label, the income reaching coffee-growing communities would meaningfully change.',
        },
        {
          flag: '🇪🇸 Spain · Almería province',
          name: 'Mohamed, 27',
          role: 'Greenhouse worker, harvesting peppers and tomatoes',
          paragraphs: [
            'Mohamed crossed from Morocco four years ago. He works in what Spanish farmers call "the sea of plastic", 30,000 hectares of greenhouses near Almería that supply much of Europe\'s winter vegetables. The work is hot, the chemicals are sometimes uncomfortable, the wages are below the Spanish minimum if you ask the wrong questions. He sends most of his earnings home to his mother and younger brothers.',
            "The peppers and tomatoes you eat in December almost certainly come from greenhouses where Mohamed or someone like him works. Spain has improved labour inspections in recent years, but the system has structural problems that one inspector can't solve.",
          ],
          matters: "Eating seasonal vegetables (Swiss tomatoes in summer rather than winter) means buying out of Almería's system. Not because Mohamed shouldn't have a job, but because better jobs are possible elsewhere in the same system.",
        },
        {
          flag: '🇪🇨 Ecuador · El Oro province',
          name: 'Lucia, 34',
          role: 'Banana farm worker, packing-house',
          paragraphs: [
            "Lucia works in the packing house of a medium-sized banana plantation. She inspects, sorts, and packs about 2,000 bananas per shift. Her husband works the fields. They have two children. The plantation uses pesticides that, under Swiss law, would not be permitted. Banana exports are critical to Ecuador's economy; they're also a source of chronic health concerns for the workers.",
            "You eat about 4 bananas a week, around 200 a year. If those bananas carry a fair-trade or organic label, Lucia's situation is meaningfully different: stricter pesticide rules, higher wages, healthcare contributions. Without those labels, she's at the mercy of a market that prizes 99-centime bananas in Swiss supermarkets.",
          ],
          matters: "Bananas with a Max Havelaar or Bio label cost about 30 to 40 centimes more per kilogram. That's the difference between Lucia's children going to school regularly or not.",
        },
      ],
      mapItems: [
        { flag: '🇨🇭 Switzerland', food: 'Dairy, eggs, potatoes, some bread, some vegetables, apples, pears, some pork and beef', note: 'The backbone of your diet by mass. Strong animal welfare, environmental rules.' },
        { flag: '🇩🇪🇫🇷🇮🇹 Germany, France, Italy', food: 'Bread wheat, pasta, some vegetables, processed meat, wines', note: 'Short trips, modest footprint. Generally good labour conditions, mixed environmental ones.' },
        { flag: '🇪🇸 Spain', food: 'Out-of-season tomatoes, peppers, citrus fruit, olive oil', note: 'Significant share of winter vegetables. Labour and water concerns in some regions.' },
        { flag: '🇧🇷🇻🇳🇪🇹 Brazil, Vietnam, Ethiopia', food: 'Coffee, indirectly some soy used for animal feed', note: 'Tropical agriculture. Sustainability outcomes depend heavily on labels and direct-trade choices.' },
        { flag: '🇪🇨🇨🇷🇨🇴 Ecuador, Costa Rica, Colombia', food: 'Bananas, some tropical fruit', note: 'Plantation-style agriculture, pesticide concerns. Fair-trade options improve the picture.' },
        { flag: '🇲🇦 Morocco', food: 'Off-season tomatoes, citrus, green beans', note: 'Increasing share of European winter veg. Water-stressed region.' },
        { flag: '🇦🇷🇧🇷 Argentina, Brazil (indirect)', food: 'Soybean meal feeding Swiss and EU dairy and livestock', note: 'Largely invisible. Linked to Amazon and Cerrado deforestation. The "soy in your milk" connection.' },
        { flag: '🇮🇳🇹🇭🇮🇩 India, Thailand, Indonesia', food: 'Rice, spices, some processed-food ingredients including palm oil', note: 'Diverse, often invisible. Palm oil in particular has biodiversity implications.' },
      ],
      sdgs: [
        { num: 2, title: 'Zero Hunger', body: 'Your meat and dairy use more land than a plant-based meal would. The land could feed more people. Plant proteins close part of this gap.' },
        { num: 3, title: 'Good Health', body: 'Your nutrition score is 76 of 100. Strong vegetables and water, lighter on whole grains and legumes. Health is doing well.' },
        { num: 5, title: 'Gender Equality', body: "Women produce half the world's food but own under 20 % of farmland. Fair-trade coffee and chocolate move that needle." },
        { num: 6, title: 'Clean Water', body: 'Your year of eating uses about 1,420 m³ of water, most of it falling on fields abroad. Seasonal vegetables ease pressure on water-stressed regions.' },
        { num: 8, title: 'Decent Work', body: "Maria's coffee, Lucia's bananas, Mohamed's peppers: the labels you choose change their working conditions, slowly." },
        { num: 12, title: 'Responsible Consumption', body: 'Swiss households throw away about 330 kg of food per year. Cutting that in half is one of the highest-impact single actions you can take.' },
        { num: 13, title: 'Climate Action', body: 'Your diet alone is 2.1 t CO₂e per year. About a quarter of your total climate footprint. Diet is the most flexible part.' },
        { num: 14, title: 'Life Below Water', body: 'You currently eat no fish, which means zero direct pressure on ocean ecosystems. Adding sustainably caught fish later would be possible.' },
        { num: 15, title: 'Life on Land', body: 'Cattle feed and coffee plantations are the biggest forest-loss drivers your diet touches. Bio-label products mostly avoid this connection.' },
        { num: 17, title: 'Partnerships', body: 'The food system is global. Solving its problems takes cooperation across borders. Your daily choices are part of that quiet cooperation.' },
      ],
      facts: [
        {
          title: 'Your coffee habit has its own climate signature.',
          paragraphs: [
            'Coffee is one of the highest-impact things in any Western diet, per calorie consumed. Your 4 cups a day add up to about 320 kg of CO₂e per year, comparable to running your fridge for a year. The drivers are tropical land use, fertiliser, drying, shipping, and home-machine energy. Capsules in particular roughly double the per-cup footprint compared with filter coffee.',
            "This isn't a suggestion to drink less coffee. Coffee is one of life's reliable pleasures. But it's worth knowing that filter coffee from a fair-trade label is, for the same number of cups, dramatically better for both the people who grow it and the planet they grow it on.",
          ],
        },
        {
          title: 'Your dairy is the biggest single climate item on your plate.',
          paragraphs: [
            'About 27 % of your dietary footprint comes from dairy: milk, yoghurt, cheese, and especially hard cheese, which is among the highest-emission foods per kilogram. The reason is methane: cows produce it as part of their digestion, and methane is a potent greenhouse gas.',
            "The good news is that Swiss dairy is one of the most sustainable systems in the world per kilogram produced. Cows here live on pasture, get good care, and the milk doesn't travel far. The less good news is that Swiss dairy still feeds on a lot of imported soybean meal, which connects your morning yoghurt, very faintly, to forests in Brazil.",
          ],
        },
        {
          title: 'A lentil is one of the most efficient foods on Earth.',
          paragraphs: [
            'To produce 1 kg of beef protein, you need roughly 25 kg of plant feed, plus several thousand litres of water, and around 50 m² of land per year. To produce 1 kg of lentil protein, you need maybe 1 kg of seed, very little water, and 5 m² of land. Lentils even fix nitrogen back into the soil, leaving the field more fertile than they found it.',
            'You currently eat lentils, chickpeas, beans, or tofu less than once a week, on average. Adding two meals a week of legumes would change your sustainability score more than any other single shift available to you. It would also save money.',
          ],
        },
        {
          title: 'Switzerland throws away one-third of the food it buys.',
          paragraphs: [
            "The average Swiss household throws away around 330 kg of food per year, much of which was perfectly edible. If you're average, that's roughly 160 kg coming out of your kitchen. Eliminating that waste, alone, would lower your annual diet-related emissions by an estimated 400 kg of CO₂e, equivalent to skipping a short-haul flight every year.",
            'Most of this waste is small things: a half-eaten salad, a tub of yoghurt one day past its date, a piece of bread that got too dry. None of it is dramatic. All of it adds up.',
          ],
        },
        {
          title: '"Sell-by" and "best-before" mean different things.',
          paragraphs: [
            '"Verbrauchen bis" or "use by" is a safety date: don\'t eat the food after this. "Mindestens haltbar bis" or "best before" is a quality date: the food is fine to eat, it might just not be at its peak. Most yoghurt, cheese, and packaged food carries the second, more flexible label. A yoghurt 3 days past its best-before date is almost always perfectly safe; one past its use-by date may not be.',
            'Learning to read these two labels properly is, no exaggeration, one of the highest-impact pieces of food literacy a Swiss household can acquire.',
          ],
        },
      ],
      choices: [
        { tag: 'Easy', tagClass: '', title: 'Add lentils, chickpeas, or beans twice a week.', body: 'A lentil dahl on Tuesdays. A chickpea curry on Fridays. Hummus on toast for lunch on Sundays. Two meals a week is all it takes. Cheaper than meat, faster than you think, and the largest single climate win on this list.', impact: 'Saves about 180 kg CO₂e per year' },
        { tag: 'Easy', tagClass: '', title: 'Switch white bread to wholemeal.', body: 'Same loaf, different aisle. Wholemeal bread has slightly higher nutrition value and slightly lower climate footprint. The flavour grows on you fast. After a month, white bread starts to taste a bit like cardboard.', impact: 'Saves about 40 kg CO₂e per year' },
        { tag: 'Easy', tagClass: '', title: 'Buy fair-trade coffee for your home cups.', body: 'You drink 4 cups a day, say 1,460 a year. Even switching half of those to a Max Havelaar or direct-trade label would change the income reaching coffee-growing communities. Filter coffee made at home is also dramatically better than capsules, environmentally.', impact: 'Better for Maria; saves ~100 kg CO₂e if you also switch from capsules' },
        { tag: 'Medium', tagClass: 'medium', title: 'Eat seasonal vegetables and fruit.', body: 'Swiss vegetables in summer, root vegetables and stored apples in winter. The Migros and Coop labels make this easier than it sounds. Apps like "Saisonküche" tell you what\'s in season this week.', impact: 'Saves about 180 kg CO₂e per year' },
        { tag: 'Medium', tagClass: 'medium', title: 'Halve your processed meat, add fish once a week.', body: 'You currently eat processed meat 3 times a week. Going to 1 to 2 reduces both your health risks and your footprint. Adding one weekly fish meal (sustainably caught, like MSC-labelled, or Swiss lake fish) closes a nutrition gap and barely moves your climate impact.', impact: 'Saves about 130 kg CO₂e per year, and you get more omega-3' },
        { tag: 'Medium', tagClass: 'medium', title: 'Choose Swiss origin for all animal products.', body: 'Buy Swiss meat. Buy Swiss dairy. Read labels. The label "Swiss Garantie" or "Naturafarm" or "Bio Suisse Knospe" means the animal lived under the strictest welfare law on the planet. Climate impact is similar to imports; animal welfare impact is enormous.', impact: 'Better for the animals; similar climate impact' },
        { tag: 'Harder', tagClass: 'hard', title: 'Halve your household food waste.', body: 'Look in your fridge before shopping. Plan meals around what\'s already there. Take leftovers to work. Use up the wilted lettuce in a stir-fry. Learn what "best-before" actually means. It\'s a habit, not a heroic act.', impact: 'Saves about 400 kg CO₂e per year and around CHF 600' },
        { tag: 'Harder', tagClass: 'hard', title: 'Aim for the planetary health diet on most days.', body: 'Mostly plants, modest dairy and meat, lots of legumes and whole grains, one or two fish meals a week. The official EAT-Lancet diet is closer to a Mediterranean-Swiss diet than to a strict vegan one. Easier than it sounds, except when eating out.', impact: 'Saves about 900 kg CO₂e per year, ~ 44 % of your diet footprint' },
      ],
      weekDays: [
        { name: 'Mon', icon: '🥗', text: 'Meatless Monday. Pasta with seasonal veg.' },
        { name: 'Tue', icon: '🍳', text: 'Eggs for breakfast, normal lunch and dinner.' },
        { name: 'Wed', icon: '🌱', text: 'Lentil dahl or chickpea curry for dinner.' },
        { name: 'Thu', icon: '🥩', text: 'Swiss-origin meat dinner if you want it.' },
        { name: 'Fri', icon: '🐟', text: 'Fish on Friday. Salmon, char, or perch.' },
        { name: 'Sat', icon: '🍕', text: 'Whatever you feel like. Saturday is Saturday.' },
        { name: 'Sun', icon: '🥖', text: 'Family lunch. Local cheese, wholemeal bread, fruit.' },
      ],
    };
  },
};
</script>

<style scoped>
.results10-wrap {
  --ink: #1A1A1A;
  --ink-soft: #2D2D2D;
  --ink-mute: #6B6B6B;
  --ink-dim: #9C9C9C;
  --rule: #DDDDDD;
  --paper: #FAFAF7;
  --card: #FFFFFF;
  --warm: #F4EEE5;
  --cream: #FBF7EF;
  --good: #4C9F38;
  --good-bg: #DDEDE0;
  --warn: #DDA63A;
  --warn-bg: #F5E4C5;
  --bad: #E5243B;
  --bad-bg: #F8D3D8;
  --leaf: #56C02B;
  --leaf-bg: #DEEFCC;
  --sky: #26BDE2;
  --sky-bg: #CDEDF6;
  --soil: #8B6B47;
  --soil-bg: #E7DCCB;
  --gold: #FCC30B;
  --gold-bg: #FEF3C7;

  --sdg2: #DDA63A;
  --sdg3: #4C9F38;
  --sdg5: #FF3A21;
  --sdg6: #26BDE2;
  --sdg8: #A21942;
  --sdg12: #BF8B2E;
  --sdg13: #3F7E44;
  --sdg14: #0A97D9;
  --sdg15: #56C02B;
  --sdg17: #19486A;

  --mono: 'JetBrains Mono', 'SF Mono', Consolas, monospace;

  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 15px;
  line-height: 1.65;
  color: var(--ink);
  background: var(--paper);
  min-height: 100%;
}
.results10-wrap *,
.results10-wrap *::before,
.results10-wrap *::after {
  box-sizing: border-box;
}

/* TOPBAR */
.topbar {
  background: var(--ink);
  color: white;
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.08em;
  padding: 12px 32px;
}
.topbar-inner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 920px;
  margin: 0 auto;
}
.brand { display: flex; align-items: center; gap: 12px; }
.brand-mark { width: 18px; height: 18px; background: var(--leaf); }
.brand-text { font-family: 'Inter', sans-serif; font-weight: 600; font-size: 14px; }
.brand-text small {
  display: block;
  font-weight: 400;
  font-size: 10px;
  letter-spacing: 0.04em;
  color: var(--ink-dim);
  margin-top: -2px;
}
.topbar-date { font-size: 11px; color: rgba(255,255,255,0.6); }

/* HERO */
.hero {
  background: linear-gradient(180deg, var(--cream) 0%, var(--paper) 100%);
  padding: 60px 32px 50px 32px;
}
.hero-inner { max-width: 920px; margin: 0 auto; }
.hero-eyebrow {
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--leaf);
  margin-bottom: 18px;
}
.hero-title {
  font-size: 44px;
  font-weight: 600;
  line-height: 1.1;
  margin: 0 0 18px 0;
  letter-spacing: -0.015em;
  max-width: 740px;
}
.hero-title em { font-style: italic; color: var(--leaf); }
.hero-sub {
  color: var(--ink-soft);
  font-size: 17px;
  max-width: 660px;
  margin: 0;
  line-height: 1.6;
}

/* SECTION */
.section {
  padding: 48px 32px;
}
.section.warm { background: var(--warm); }
.section.cream { background: var(--cream); }
.section.dark { background: var(--ink); color: white; }
.section.leaf { background: var(--leaf-bg); }
.section.sky { background: var(--sky-bg); }
.section.soil { background: var(--soil-bg); }
.section-inner { max-width: 820px; margin: 0 auto; }

.section-header {
  margin-bottom: 32px;
  border-top: 2px solid var(--leaf);
  padding-top: 24px;
}
.section-eyebrow {
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ink-mute);
  margin-bottom: 10px;
}
.section.dark .section-eyebrow { color: var(--gold); }
.section-title {
  font-size: 30px;
  font-weight: 600;
  margin: 0 0 14px 0;
  line-height: 1.2;
  letter-spacing: -0.01em;
}
.section-title em { font-style: italic; color: var(--leaf); }
.section-lede {
  color: var(--ink-soft);
  font-size: 16px;
  max-width: 720px;
  margin: 0;
  line-height: 1.65;
}
.section.dark .section-lede { color: rgba(255,255,255,0.8); }

/* PROSE */
.prose {
  font-size: 16px;
  line-height: 1.7;
  color: var(--ink-soft);
  max-width: 720px;
}
.prose p { margin: 0 0 16px 0; }
.prose p:last-child { margin-bottom: 0; }
.prose strong { color: var(--ink); }
.prose em { color: var(--leaf); font-style: italic; font-weight: 500; }
.prose--spaced { margin-top: 24px; }
.prose--card { font-size: 15px; }
.prose--large { font-size: 17px; }

/* PULL QUOTE */
.pull {
  font-size: 22px;
  line-height: 1.4;
  color: var(--ink);
  font-style: italic;
  border-left: 4px solid var(--leaf);
  padding: 12px 24px;
  margin: 28px 0;
  max-width: 700px;
}
.pull--large { margin-top: 36px; }
.pull-attrib {
  font-size: 13px;
  color: var(--ink-mute);
  font-style: normal;
  margin-top: 8px;
  font-family: var(--mono);
  letter-spacing: 0.04em;
}

/* CARD */
.card {
  background: var(--card);
  border-radius: 8px;
  padding: 28px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}
.card-eyebrow {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-mute);
  margin-bottom: 6px;
}
.card-title {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 12px;
  letter-spacing: -0.005em;
}

/* HEADLINE STAT */
.stat-hero {
  background: var(--card);
  border-radius: 12px;
  padding: 36px;
  margin-bottom: 32px;
  text-align: center;
  border: 1px solid var(--rule);
}
.stat-hero-label {
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ink-mute);
  margin-bottom: 14px;
}
.stat-hero-value {
  font-size: 80px;
  font-weight: 600;
  line-height: 1;
  color: var(--leaf);
  margin-bottom: 14px;
  letter-spacing: -0.02em;
}
.stat-hero-value .unit {
  font-size: 24px;
  color: var(--ink-mute);
  font-weight: 400;
  margin-left: 4px;
}
.stat-hero-body {
  font-size: 16px;
  color: var(--ink-soft);
  max-width: 540px;
  margin: 0 auto;
  line-height: 1.6;
}

/* THREE STATS */
.three-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  margin-bottom: 20px;
}
.stat-tile {
  background: var(--card);
  border-radius: 8px;
  padding: 22px;
  border: 1px solid var(--rule);
  text-align: center;
}
.stat-tile-icon {
  font-size: 28px;
  margin-bottom: 10px;
}
.stat-tile-value {
  font-size: 34px;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 4px;
}
.stat-tile-value.good { color: var(--good); }
.stat-tile-value.warn { color: var(--warn); }
.stat-tile-value.bad { color: var(--bad); }
.stat-tile-value .unit {
  font-size: 14px;
  color: var(--ink-mute);
  font-weight: 400;
}
.stat-tile-label {
  font-size: 13px;
  color: var(--ink-mute);
  line-height: 1.5;
}

/* WINS */
.wins-list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}
.win {
  background: var(--card);
  border-radius: 8px;
  padding: 20px 24px;
  border-left: 4px solid var(--good);
  display: grid;
  grid-template-columns: 60px 1fr;
  gap: 18px;
  align-items: start;
}
.win-icon {
  font-size: 32px;
  text-align: center;
  padding-top: 4px;
}
.win-title {
  font-size: 17px;
  font-weight: 600;
  margin-bottom: 6px;
}
.win-body {
  font-size: 14px;
  color: var(--ink-soft);
  line-height: 1.6;
}

/* STORY CARDS */
.story-card {
  background: var(--card);
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 20px;
  border: 1px solid var(--rule);
}
.story-header {
  padding: 18px 24px;
  background: var(--soil-bg);
  border-bottom: 1px solid var(--rule);
}
.story-flag {
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.08em;
  color: var(--ink-mute);
  margin-bottom: 4px;
}
.story-name {
  font-size: 19px;
  font-weight: 600;
  margin: 0;
}
.story-role {
  font-size: 14px;
  color: var(--ink-mute);
  margin-top: 2px;
}
.story-body {
  padding: 24px;
  font-size: 14px;
  line-height: 1.7;
  color: var(--ink-soft);
}
.story-body p { margin: 0 0 12px 0; }
.story-body p:last-child { margin-bottom: 0; }
.story-link {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--rule);
  font-size: 13px;
  color: var(--ink-mute);
}
.story-link strong { color: var(--ink); }

/* DARK section overrides */
.section.dark .card { background: rgba(255,255,255,0.05); color: white; border: 1px solid rgba(255,255,255,0.1); }
.section.dark .card-title { color: white; }
.section.dark .prose { color: rgba(255,255,255,0.85); }
.section.dark .prose strong { color: white; }
.section.dark .pull { color: white; border-left-color: var(--gold); }
.section.dark .pull-attrib { color: rgba(255,255,255,0.6); }

/* SDG GRID */
.sdg-intro {
  background: var(--card);
  border-radius: 8px;
  padding: 22px 26px;
  margin-bottom: 20px;
  font-size: 15px;
  color: var(--ink-soft);
  line-height: 1.65;
}
.sdg-intro p { margin: 0; }
.sdg-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
}
.sdg-tile {
  border-radius: 8px;
  overflow: hidden;
  color: white;
  position: relative;
  min-height: 140px;
  padding: 14px 16px 18px 16px;
  display: flex;
  flex-direction: column;
}
.sdg-num {
  font-family: var(--mono);
  font-weight: 700;
  font-size: 22px;
  margin-bottom: 4px;
  letter-spacing: -0.01em;
}
.sdg-title {
  font-weight: 700;
  font-size: 14px;
  margin-bottom: 10px;
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.sdg-body {
  font-size: 12px;
  line-height: 1.45;
  font-weight: 400;
  margin-top: auto;
  background: rgba(0,0,0,0.18);
  padding: 8px 10px;
  border-radius: 4px;
}
.sdg-tile.sdg-2 { background: var(--sdg2); }
.sdg-tile.sdg-3 { background: var(--sdg3); }
.sdg-tile.sdg-5 { background: var(--sdg5); }
.sdg-tile.sdg-6 { background: var(--sdg6); }
.sdg-tile.sdg-8 { background: var(--sdg8); }
.sdg-tile.sdg-12 { background: var(--sdg12); }
.sdg-tile.sdg-13 { background: var(--sdg13); }
.sdg-tile.sdg-14 { background: var(--sdg14); }
.sdg-tile.sdg-15 { background: var(--sdg15); }
.sdg-tile.sdg-17 { background: var(--sdg17); }

/* CHOICE CARDS */
.choices-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.choice {
  background: var(--card);
  border-radius: 10px;
  padding: 22px 24px;
  border: 1px solid var(--rule);
}
.choice-tag {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 3px 9px;
  background: var(--leaf-bg);
  color: var(--good);
  border-radius: 3px;
  display: inline-block;
  margin-bottom: 12px;
}
.choice-tag.medium { background: var(--gold-bg); color: var(--warn); }
.choice-tag.hard { background: var(--bad-bg); color: var(--bad); }
.choice-title {
  font-size: 17px;
  font-weight: 600;
  margin-bottom: 8px;
  letter-spacing: -0.005em;
}
.choice-body {
  font-size: 14px;
  color: var(--ink-soft);
  line-height: 1.65;
  margin-bottom: 14px;
}
.choice-impact {
  font-size: 13px;
  font-family: var(--mono);
  color: var(--good);
  padding-top: 12px;
  border-top: 1px solid var(--rule);
}

/* FOOTPRINT */
.footprint-strip {
  background: var(--card);
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 20px;
}
.footprint-row {
  display: grid;
  grid-template-columns: 140px 1fr 100px;
  gap: 14px;
  padding: 14px 0;
  border-bottom: 1px solid var(--rule);
  align-items: center;
}
.footprint-row:first-child { padding-top: 0; }
.footprint-row:last-child { border-bottom: none; padding-bottom: 0; }
.footprint-name {
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
}
.footprint-name-icon { font-size: 22px; }
.footprint-bar {
  background: var(--warm);
  height: 18px;
  border-radius: 4px;
  position: relative;
  overflow: hidden;
}
.footprint-bar-fill { height: 100%; border-radius: 4px; }
.footprint-bar-fill.lg { background: var(--bad); }
.footprint-bar-fill.md { background: var(--warn); }
.footprint-bar-fill.sm { background: var(--good); }
.footprint-bar-fill.xs { background: var(--leaf); }
.footprint-value {
  font-family: var(--mono);
  font-size: 13px;
  font-weight: 600;
  text-align: right;
}

/* MAP */
.map-card {
  background: var(--card);
  border-radius: 10px;
  padding: 28px;
  margin-bottom: 20px;
}
.map-intro {
  font-size: 15px;
  color: var(--ink-soft);
  line-height: 1.65;
  margin-bottom: 18px;
}
.map-list {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.map-item {
  padding: 16px 18px;
  background: var(--cream);
  border-radius: 6px;
  border-left: 3px solid var(--soil);
}
.map-flag {
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.06em;
  color: var(--ink-mute);
  margin-bottom: 6px;
  text-transform: uppercase;
}
.map-food { font-weight: 600; font-size: 15px; margin-bottom: 4px; }
.map-note { font-size: 13px; color: var(--ink-soft); line-height: 1.55; }

/* PROMISE */
.promise {
  background: var(--cream);
  border-radius: 12px;
  padding: 32px 36px;
  margin: 24px 0;
  border: 2px solid var(--leaf);
}
.promise-eyebrow {
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--leaf);
  margin-bottom: 10px;
}
.promise-title {
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 14px;
  letter-spacing: -0.01em;
}
.promise-body {
  font-size: 15px;
  line-height: 1.7;
  color: var(--ink-soft);
}
.promise-body p { margin: 0 0 12px 0; }
.promise-body p:last-child { margin-bottom: 0; }

/* WEEK */
.week-intro {
  font-size: 15px;
  color: var(--ink-soft);
  margin: 0 0 12px 0;
}
.week-footer {
  font-size: 14px;
  color: var(--ink-soft);
  margin: 18px 0 0 0;
}
.week-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
  margin-top: 18px;
}
.day {
  background: var(--cream);
  border-radius: 6px;
  padding: 12px;
  text-align: center;
  border: 1px solid var(--rule);
}
.day-name {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.06em;
  color: var(--ink-mute);
  margin-bottom: 6px;
  text-transform: uppercase;
}
.day-icon { font-size: 22px; margin-bottom: 6px; }
.day-text { font-size: 11px; color: var(--ink-soft); line-height: 1.4; }

/* FOOTER */
.footer {
  background: var(--ink);
  color: white;
  padding: 40px 32px;
  font-size: 13px;
}
.footer-inner {
  max-width: 820px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 24px;
}
.footer p {
  margin: 10px 0 0 0;
  color: rgba(255,255,255,0.7);
  max-width: 540px;
  line-height: 1.65;
}
.footer-meta {
  font-family: var(--mono);
  font-size: 11px;
  color: rgba(255,255,255,0.55);
  text-align: right;
  line-height: 1.6;
}

/* PRINT */
@media print {
  .results10-wrap { background: white; }
  .topbar { background: white; color: black; }
  .hero, .card, .stat-hero, .stat-tile, .story-card, .footprint-strip, .choice, .promise, .sdg-intro, .map-card { background: white; box-shadow: none; }
  .section { padding: 24px; }
  .section.dark { background: white; color: black; }
}
</style>
